import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Video, Users, UserCircle2, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, startGoogleLogin } from "../lib/auth";

const WEEKLY_AGENDA = [
  { key: "promises_kept", title: "Promises Kept", minutes: 5 },
  { key: "wins", title: "Wins", minutes: 5 },
  { key: "losses", title: "Losses & Lessons", minutes: 5 },
  { key: "goals", title: "Goals Review", minutes: 5 },
  { key: "challenges", title: "Challenges", minutes: 5 },
  { key: "reflection", title: "Reflection — what this week says about me", minutes: 5 },
  { key: "action_items", title: "Action Items", minutes: 5 },
];

// Monthly is a board review, not a stand-up — 5 extra deep-dive sections and
// longer default time-boxes. Mirrors backend `MONTHLY_AGENDA_SECTIONS`.
const MONTHLY_AGENDA = [
  { key: "promises_kept", title: "Promises Kept", minutes: 8 },
  { key: "wins", title: "Wins", minutes: 8 },
  { key: "losses", title: "Losses & Lessons", minutes: 8 },
  { key: "goals", title: "Goals Review", minutes: 10 },
  { key: "intent_vs_reality", title: "Intent vs Reality", minutes: 8 },
  { key: "financial_review", title: "Financial Review", minutes: 10 },
  { key: "metrics_deep_dive", title: "Metrics Deep-Dive", minutes: 10 },
  { key: "habits_audit", title: "Habits Audit", minutes: 8 },
  { key: "challenges", title: "Challenges", minutes: 8 },
  { key: "strategic_pivots", title: "Strategic Pivots", minutes: 10 },
  { key: "big_picture", title: "Big-Picture Reflection", minutes: 10 },
  { key: "action_items", title: "Action Items", minutes: 8 },
];

// Quarterly is a board-of-directors 90-day review. Mirrors backend
// `QUARTERLY_AGENDA_SECTIONS`.
const QUARTERLY_AGENDA = [
  { key: "promises_kept", title: "Promises Kept", minutes: 10 },
  { key: "wins", title: "Wins of the Quarter", minutes: 10 },
  { key: "losses", title: "Losses & Lessons", minutes: 10 },
  { key: "goals", title: "Quarter Goals Review", minutes: 10 },
  { key: "intent_vs_reality", title: "Intent vs Reality", minutes: 10 },
  { key: "financial_review", title: "Financial Review (P&L, runway)", minutes: 12 },
  { key: "metrics_deep_dive", title: "Metrics Deep-Dive", minutes: 12 },
  { key: "habits_audit", title: "Habits Audit (90-day)", minutes: 10 },
  { key: "category_reflection", title: "Category Reflection (5 Pillars)", minutes: 12 },
  { key: "strategic_pivots", title: "Strategic Pivots & Kills", minutes: 12 },
  { key: "vision_alignment", title: "Vision Alignment", minutes: 10 },
  { key: "next_quarter_bets", title: "Next Quarter's Bets", minutes: 12 },
  { key: "action_items", title: "Action Items", minutes: 8 },
];

// Kickoff templates open the period — mirrors the backend `*_KICKOFF_SECTIONS`.
const WEEKLY_KICKOFF_AGENDA = [
  { key: "theme", title: "Theme for the Week", minutes: 3 },
  { key: "commitments", title: "Goals I'm Naming", minutes: 5 },
  { key: "priority_one", title: "The One That Matters Most", minutes: 3 },
  { key: "schedule_habits", title: "Calendar & Habits I'm Committing To", minutes: 3 },
  { key: "risks", title: "What Could Derail Me", minutes: 3 },
];

const MONTHLY_KICKOFF_AGENDA = [
  { key: "theme", title: "Theme for the Month", minutes: 5 },
  { key: "commitments", title: "Goals I'm Naming", minutes: 10 },
  { key: "priority_one", title: "The One That Matters Most", minutes: 5 },
  { key: "targets", title: "Targets & Numbers", minutes: 8 },
  { key: "bets_vs_maintenance", title: "Bets vs Maintenance", minutes: 8 },
  { key: "schedule_habits", title: "Calendar & Habits I'm Committing To", minutes: 6 },
  { key: "risks", title: "What Could Derail Me", minutes: 6 },
];

const QUARTERLY_KICKOFF_AGENDA = [
  { key: "theme", title: "Theme for the Quarter", minutes: 8 },
  { key: "vision_check", title: "Vision Check — where this quarter has to land", minutes: 10 },
  { key: "commitments", title: "Goals I'm Naming", minutes: 12 },
  { key: "priority_one", title: "The One That Matters Most", minutes: 8 },
  { key: "targets", title: "Targets & Numbers", minutes: 10 },
  { key: "pillar_intentions", title: "Intentions by Pillar (5 Pillars)", minutes: 12 },
  { key: "bets_vs_maintenance", title: "Bets vs Maintenance", minutes: 10 },
  { key: "saying_no", title: "What I'm Saying No To", minutes: 8 },
  { key: "schedule_habits", title: "Calendar & Habits I'm Committing To", minutes: 8 },
  { key: "risks", title: "What Could Derail Me", minutes: 8 },
];

const AGENDA_BY_CADENCE = {
  weekly: WEEKLY_AGENDA,
  monthly: MONTHLY_AGENDA,
  quarterly: QUARTERLY_AGENDA,
  weekly_kickoff: WEEKLY_KICKOFF_AGENDA,
  monthly_kickoff: MONTHLY_KICKOFF_AGENDA,
  quarterly_kickoff: QUARTERLY_KICKOFF_AGENDA,
};
const CADENCE_OPTIONS = Object.keys(AGENDA_BY_CADENCE);
const PERIODS = [
  { value: "weekly", label: "Week" },
  { value: "monthly", label: "Month" },
  { value: "quarterly", label: "Quarter" },
];
const CAPTIONS = {
  weekly_kickoff: "5-section kickoff · ~17 min · name the week's goals",
  monthly_kickoff: "7-section kickoff · ~48 min · targets, bets and guardrails",
  quarterly_kickoff: "10-section kickoff · ~94 min · vision, pillars and what you're saying no to",
  weekly: "7-section review · ~35 min · scores the week's kickoff",
  monthly: "12-section board review · ~96 min · rolls up the month's weeklies",
  quarterly: "13-section board review · ~2 hrs · rolls up the quarter's monthlies",
};

export default function NewMeeting() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCadence = CADENCE_OPTIONS.includes(searchParams.get("cadence")) ? searchParams.get("cadence") : "weekly";
  const initialType = ["solo", "partner"].includes(searchParams.get("type")) ? searchParams.get("type") : "solo";
  const [title, setTitle] = useState("");
  const [type, setType] = useState(initialType);
  const [mode, setMode] = useState(initialCadence.endsWith("_kickoff") ? "kickoff" : "review");
  const [period, setPeriod] = useState(initialCadence.replace("_kickoff", ""));
  const cadence = mode === "kickoff" ? `${period}_kickoff` : period;
  const [scheduledFor, setScheduledFor] = useState("");
  const [agenda, setAgenda] = useState(() => AGENDA_BY_CADENCE[initialCadence].map((s) => ({ ...s })));
  const [submitting, setSubmitting] = useState(false);

  // Whenever the cadence flips, swap in the correct template (unless the user
  // has already customised minutes — track that with a "dirty" flag). For now
  // we always swap: monthly requires the deeper deck; if the user was mid-tune
  // they'll re-tune. Simpler + safer than a diff-merge.
  useEffect(() => {
    setAgenda(AGENDA_BY_CADENCE[cadence].map((s) => ({ ...s })));
  }, [cadence]);

  const updateMinutes = (i, v) => {
    setAgenda((prev) => prev.map((s, idx) => (idx === i ? { ...s, minutes: Math.max(1, Number(v) || 1) } : s)));
  };

  const create = async () => {
    if (!title.trim()) {
      toast.error("Give the session a title");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/meetings", {
        title: title.trim(),
        meeting_type: type,
        cadence,
        scheduled_for: scheduledFor || null,
        agenda,
      });
      // A future-dated session shouldn't drop the user into the room — send
      // them to the archive where they can add it to their calendar.
      const future = scheduledFor && new Date(scheduledFor).getTime() > Date.now() + 2 * 60 * 1000;
      if (future) {
        toast.success("Session scheduled. Add it to your calendar from here.");
        nav("/meetings");
      } else {
        toast.success("Session scheduled. Calling to order…");
        nav(`/meetings/${res.data.id}`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not create session");
    } finally {
      setSubmitting(false);
    }
  };

  const totalMinutes = agenda.reduce((acc, s) => acc + s.minutes, 0);
  const { user: authUser, loading: authLoading } = useAuth();
  const showSigninBanner = !authLoading && !authUser;

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-meeting-root">
      <p className="label-eyebrow">Session setup</p>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Call the meeting to order.</h1>
      <p className="text-[#888] mt-4 max-w-xl">
        Configure the agenda for this session. The chair (you) is responsible for keeping time.
      </p>

      {showSigninBanner && (
        <div
          className="mt-8 rounded-md border border-[#c89f45]/40 bg-[#c89f45]/5 p-4 flex flex-col sm:flex-row sm:items-start gap-3"
          data-testid="new-meeting-signin-banner"
        >
          <ShieldCheck size={18} className="text-[#c89f45] mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-[#eee] font-medium">Sign in before you record.</p>
            <p className="text-xs text-[#888] mt-1 leading-relaxed">
              Anonymous sessions can be lost if you clear cookies, sign out, or switch devices. Sign in with Google
              and this session — plus every one after — will follow you across every device.
            </p>
          </div>
          <Button
            onClick={() => startGoogleLogin()}
            className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md h-11 sm:h-9 px-4 shrink-0 w-full sm:w-auto"
            data-testid="new-meeting-banner-signin"
          >
            Sign in
          </Button>
        </div>
      )}

      <div className="mt-12 space-y-10">
        {/* Title */}
        <div>
          <Label className="label-eyebrow" htmlFor="title">Session title</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Q1 Week 4 — Personal Board Meeting"
            className="mt-3 bg-[#121212] border-[#2a2a2a] text-[#fafafa] rounded-md h-12 focus:border-[#c89f45] focus:ring-0"
            data-testid="meeting-title-input"
          />
        </div>

        {/* Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setType("solo")}
            data-testid="meeting-type-solo"
            className={`text-left p-6 rounded-md border transition-colors ${
              type === "solo" ? "border-[#c89f45] bg-[#121212]" : "border-[#2a2a2a] bg-[#121212] hover:border-[#3a3a3a]"
            }`}
          >
            <UserCircle2 size={20} className="text-[#c89f45]" />
            <p className="font-serif text-xl mt-3">Solo session</p>
            <p className="text-xs text-[#888] mt-1">You vs. you. The hardest board member to please.</p>
          </button>
          <button
            type="button"
            onClick={() => setType("partner")}
            data-testid="meeting-type-partner"
            className={`text-left p-6 rounded-md border transition-colors ${
              type === "partner" ? "border-[#c89f45] bg-[#121212]" : "border-[#2a2a2a] bg-[#121212] hover:border-[#3a3a3a]"
            }`}
          >
            <Users size={20} className="text-[#c89f45]" />
            <p className="font-serif text-xl mt-3">With partner / group</p>
            <p className="text-xs text-[#888] mt-1">Invite an accountability partner once recording begins.</p>
          </button>
        </div>

        {/* Kickoff or review, then the period */}
        <div>
          <p className="label-eyebrow mb-3">Session type</p>
          <div className="inline-flex rounded-md border border-[#2a2a2a] p-1 bg-[#121212]">
            {[
              { value: "kickoff", label: "Kickoff" },
              { value: "review", label: "Review" },
            ].map((m) => (
              <button
                key={m.value}
                onClick={() => setMode(m.value)}
                data-testid={`mode-${m.value}`}
                className={`px-5 py-2 min-h-[40px] rounded text-sm transition-colors ${
                  mode === m.value ? "bg-[#c89f45] text-[#0a0a0a]" : "text-[#aaa] hover:text-white"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#8a8a8a] mt-2.5">
            {mode === "kickoff"
              ? "Opens the period — you name the goals you're going after."
              : "Closes the period — scores what you named at kickoff."}
          </p>

          <p className="label-eyebrow mb-3 mt-6">Period</p>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`period-${p.value}`}
                className={`px-4 py-2.5 min-h-[44px] sm:min-h-0 sm:py-2 rounded-md text-sm transition-colors ${
                  period === p.value
                    ? "bg-[#c89f45] text-[#0a0a0a]"
                    : "bg-[#121212] border border-[#2a2a2a] text-[#aaa] hover:bg-[#1a1a1a] hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#8a8a8a] mt-3 font-mono" data-testid="cadence-caption">
            {CAPTIONS[cadence]}
          </p>
        </div>

        {/* Scheduled for (optional) */}
        <div>
          <Label className="label-eyebrow" htmlFor="scheduled-for">Scheduled for (optional — adds to calendar)</Label>
          <Input
            id="scheduled-for"
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            className="mt-3 bg-[#121212] border-[#2a2a2a] text-[#fafafa] rounded-md h-12 focus:border-[#c89f45] focus:ring-0 max-w-xs"
            data-testid="meeting-scheduled-input"
          />
        </div>

        {/* Agenda */}
        <div>
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="label-eyebrow">Agenda</p>
              <h3 className="font-serif text-2xl mt-2">The five-point board agenda</h3>
            </div>
            <span className="font-mono text-sm text-[#c89f45]">{totalMinutes} MIN TOTAL</span>
          </div>
          <ul className="space-y-2">
            {agenda.map((s, i) => (
              <li key={s.key} className="surface rounded-md p-4 flex items-center justify-between" data-testid={`agenda-row-${s.key}`}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[#c89f45] text-sm w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-base">{s.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    value={s.minutes}
                    onChange={(e) => updateMinutes(i, e.target.value)}
                    className="w-20 h-11 sm:h-9 bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] font-mono text-base sm:text-sm focus:border-[#c89f45]"
                    data-testid={`agenda-minutes-${s.key}`}
                  />
                  <span className="text-xs text-[#8a8a8a] font-mono">MIN</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-3 text-xs text-[#8a8a8a]">
          <Video size={14} /> <Mic size={14} />
          <span>Camera and microphone permissions will be requested when the session opens.</span>
        </div>

        <div className="pt-2">
          <Button
            onClick={create}
            disabled={submitting}
            className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-12 px-6 font-medium"
            data-testid="create-meeting-button"
          >
            {submitting ? "Opening session…" : "Open session →"}
          </Button>
        </div>
      </div>
    </div>
  );
}
